import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CMFlex } from "@chartmetric/chartmetric-design-system/components/CMFlex";
import { CMText } from "@chartmetric/chartmetric-design-system/components/CMText";
import {
  AnnouncementControl,
  AnnouncementList,
  type Announcement,
  type AnnouncementCategory,
} from "../components/announcement";

import {
  fetchAnnouncementCategories,
  fetchAnnouncementList,
} from "../api/announcement";

const PAGE_SIZE = 10;

function sortPinnedFirst(list: Announcement[]): Announcement[] {
  return [...list].sort((a, b) => {
    if (a.is_pinned === b.is_pinned) return 0;
    return a.is_pinned ? -1 : 1;
  });
}

function HomePage() {
  const { t } = useTranslation("common", { keyPrefix: "announcementPanel" });

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [categories, setCategories] = useState<AnnouncementCategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchAnnouncementCategories();
        if (!cancelled) setCategories(list);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPage = useCallback(
    async (nextOffset: number, replace: boolean): Promise<void> => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const response = await fetchAnnouncementList({
          offset: nextOffset,
          limit: PAGE_SIZE,
          categories:
            selectedCategories.length > 0 ? selectedCategories : undefined,
          plans: selectedPlans.length > 0 ? selectedPlans : undefined,
        });
        if (requestId !== requestIdRef.current) return;

        const incoming = response.data ?? [];
        setAnnouncements((prev) =>
          replace
            ? sortPinnedFirst(incoming)
            : sortPinnedFirst([...prev, ...incoming]),
        );
        setOffset(nextOffset + incoming.length);
        setHasMore(incoming.length === PAGE_SIZE);
      } catch {
        if (requestId === requestIdRef.current) {
          setError(t("loadError"));
          setHasMore(false);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [selectedCategories, selectedPlans, t],
  );

  useEffect(() => {
    setAnnouncements([]);
    setOffset(0);
    setHasMore(true);
    void loadPage(0, true);
  }, [loadPage]);

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    void loadPage(offset, false);
  }, [loading, hasMore, offset, loadPage]);

  const handleReactionChange = useCallback(() => {
    // no-op: hook refreshes local counts; list state is eventually consistent
  }, []);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );

  return (
    <CMFlex
      vertical
      fullWidth
      gap="md"
      _className="mx-auto max-w-3xl px-4 py-6"
    >
      <CMFlex align="center" justify="between" gap="md">
        <CMFlex vertical>
          <CMText value={t("heading")} variant="h2" />
          <CMText value={t("subheading")} variant="mini-1" color="tertiary" />
        </CMFlex>
        <AnnouncementControl
          categories={sortedCategories}
          selectedCategories={selectedCategories}
          onCategoryFilter={setSelectedCategories}
          selectedPlans={selectedPlans}
          onSelectedPlansChange={setSelectedPlans}
        />
      </CMFlex>
      {error && (
        <CMFlex
          r="md"
          p="sm"
          border="border-cm-red-300"
          background="bg-cm-red-50"
        >
          <CMText value={error} variant="mini-1" color="error" />
        </CMFlex>
      )}
      <CMFlex>
        <AnnouncementList
          announcements={announcements}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onReactionChange={handleReactionChange}
        />
      </CMFlex>
    </CMFlex>
  );
}

export default HomePage;
